import React from 'react'
import {Box,  Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from '@mui/material'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import {formatNumber} from '../../../helpers/UtilityHelper'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE} from '../../../styles/foundation/colors'
import {SHADOW} from '../../../styles/foundation/effects'
import {helpTextSx, helpTextTypeSx} from '../../../styles/patterns'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {getPerformanceColor, PERF_COLORS} from './performanceUtils'

const statCardSx = {
  bgcolor: WHITE, borderRadius: '10px', p: '16px', textAlign: 'center',
  boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {transform: 'translateY(-2px)', boxShadow: SHADOW.cardHover},
}
const headerCellSx = {
  fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: SUBTLE,
  textTransform: 'uppercase', letterSpacing: '0.03em', py: 1.5, whiteSpace: 'nowrap',
}
const dataCellSx = {fontFamily: FONT, fontSize: SIZE.xs, py: 1.2}

function PerformanceCell({value, type = 'default'}) {
  const color = getPerformanceColor(value, type)
  return (
    <Typography sx={{
      fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color,
      bgcolor: color + '14', px: 1, py: 0.25, borderRadius: '6px', display: 'inline-block',
    }}>
      {value != null ? `${value}%` : '—'}
    </Typography>
  )
}

export default function ForwardPerformance({data = []}) {
  if (data.length === 0) return null

  const totalVol = data.reduce((s, d) => s + d.volume, 0)
  const avgDelivery = totalVol > 0
    ? (data.reduce((s, d) => s + d.deliveryPct * d.volume, 0) / totalVol).toFixed(1)
    : 0
  const avgFirstAttempt = totalVol > 0
    ? (data.reduce((s, d) => s + d.firstAttemptPct * d.volume, 0) / totalVol).toFixed(1)
    : 0
  const best = data.reduce((a, b) => a.deliveryPct > b.deliveryPct ? a : b)
  const worst = data.reduce((a, b) => a.deliveryPct < b.deliveryPct ? a : b)

  return (
    <>
      {/* KPI summary */}
      <Box sx={{display: 'flex', gap: '12px', mb: '20px', flexWrap: 'wrap'}}>
        {[
          {label: 'Total Shipments', value: formatNumber(totalVol), color: NAVY},
          {label: 'Avg Delivery %', value: `${avgDelivery}%`, color: getPerformanceColor(parseFloat(avgDelivery))},
          {label: 'Avg 1st Attempt %', value: `${avgFirstAttempt}%`, color: getPerformanceColor(parseFloat(avgFirstAttempt))},
          {label: 'Best Performer', value: best.courierName, color: PERF_COLORS.green, sub: `${best.deliveryPct}%`},
          {label: 'Needs Improvement', value: worst.courierName, color: PERF_COLORS.red, sub: `${worst.deliveryPct}%`},
        ].map((s) => (
          <Box key={s.label} sx={{...statCardSx, flex: '1 1 150px', minWidth: 140}}>
            <Typography sx={{fontSize: s.sub ? SIZE.sm : '22px', fontWeight: WEIGHT.extra, lineHeight: 1.2, color: s.color}}>
              {s.value}
            </Typography>
            {s.sub && <Typography sx={{fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: s.color, mt: 0.3}}>{s.sub}</Typography>}
            <Typography sx={{color: SUBTLE, fontSize: SIZE['2xs'], mt: '2px', fontWeight: WEIGHT.medium, fontFamily: FONT}}>{s.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Help text */}
      <Box sx={helpTextSx}>
        <InfoOutlinedIcon sx={{fontSize: 16, color: '#3783e7', mt: 0.2}} />
        <Typography sx={helpTextTypeSx}>
          <strong>Color coding:</strong> <span style={{color: PERF_COLORS.green}}>Green ≥85%</span> |{' '}
          <span style={{color: PERF_COLORS.amber}}>Amber 75–84%</span> |{' '}
          <span style={{color: PERF_COLORS.red}}>Red &lt;75%</span>.
          COD threshold: ≥80% green, ≥70% amber. TAT: ≥95% green, ≥85% amber.
        </Typography>
      </Box>

      {/* Performance table */}
      <Box sx={{bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`, overflow: 'hidden'}}>
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{bgcolor: '#f8f9fc'}}>
                <TableCell sx={headerCellSx}>#</TableCell>
                <TableCell sx={headerCellSx}>Courier</TableCell>
                <TableCell sx={headerCellSx} align='right'>Volume</TableCell>
                <TableCell sx={headerCellSx} align='right'>Delivery %</TableCell>
                <TableCell sx={headerCellSx} align='right'>1st Attempt %</TableCell>
                <TableCell sx={headerCellSx} align='right'>COD Del %</TableCell>
                <TableCell sx={headerCellSx} align='right'>Prepaid Del %</TableCell>
                <TableCell sx={headerCellSx} align='right'>RTO %</TableCell>
                <TableCell sx={headerCellSx} align='right'>TAT %</TableCell>
                <TableCell sx={headerCellSx} align='right'>TAT+1 %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, i) => (
                <TableRow key={row.courierName} sx={{'&:hover': {bgcolor: '#fafbfc'}}}>
                  <TableCell sx={dataCellSx}>
                    <Box sx={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: WEIGHT.bold, fontFamily: FONT,
                      bgcolor: i === 0 ? '#fef3c7' : i === 1 ? '#f3f4f6' : i === 2 ? '#fed7aa' : '#f9fafb',
                      color: i === 0 ? '#92400e' : i === 1 ? '#4b5563' : i === 2 ? '#9a3412' : SUBTLE,
                    }}>
                      {i + 1}
                    </Box>
                  </TableCell>
                  <TableCell sx={{...dataCellSx, fontWeight: WEIGHT.bold, color: NAVY}}>
                    {i === 0 && <EmojiEventsOutlinedIcon sx={{fontSize: 14, color: '#f59e0b', mr: 0.5, verticalAlign: 'text-bottom'}} />}
                    {row.courierName}
                  </TableCell>
                  <TableCell sx={dataCellSx} align='right'>{formatNumber(row.volume)}</TableCell>
                  <TableCell align='right'><PerformanceCell value={row.deliveryPct} /></TableCell>
                  <TableCell align='right'><PerformanceCell value={row.firstAttemptPct} /></TableCell>
                  <TableCell align='right'><PerformanceCell value={row.codDeliveryPct} type='cod' /></TableCell>
                  <TableCell align='right'><PerformanceCell value={row.prepaidDeliveryPct} type='prepaid' /></TableCell>
                  <TableCell align='right'>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: row.rtoPct > 15 ? PERF_COLORS.red : row.rtoPct > 10 ? PERF_COLORS.amber : PERF_COLORS.green}}>
                      {row.rtoPct.toFixed(1)}%
                    </Typography>
                  </TableCell>
                  <TableCell align='right'><PerformanceCell value={row.tatPct} type='tat' /></TableCell>
                  <TableCell align='right'><PerformanceCell value={row.tatPlusOnePct} type='tatPlusOne' /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  )
}
